// src/pages/EditPendentLoadBearer.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "../axiosConfig";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import "../styles/EditPendentLoadBearer.css";
import { customSelectStyles } from "../styles/reactSelectStyles";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { components as RSComponents } from "react-select";
import { useTranslation } from "react-i18next";

const MAX_LENGTH = 13.6;
const MAX_HEIGHT = 2.7;
const MAX_WIDTH = 2.5;
const MIN_LENGTH = 0.01;

const MAX_POINTS = 7;

// Helpers
const isNonEmpty = (v) => (typeof v === "string" ? v.trim() !== "" : !!v);
const pointIsComplete = (p) => {
  const handlingEquipment = String(
    p.handlingEquipment || ""
  ).trim().toUpperCase();

  return (
    isNonEmpty(p.location) &&
    !p.hourError &&
    !p.lengthErrorFlag &&
    !p.widthErrorFlag &&
    !p.heightErrorFlag &&
    !(
      handlingEquipment === "OTHER" &&
      !isNonEmpty(p.handlingEquipmentOther)
    )
  );
};

const hasAnyHiddenEmpty = () => false;

const makeEmptyPoint = () => ({
  location: "",
  date: "",
  hour: "",

  // 🆕 point-level dimensions
  length: "",
  width: "",
  height: "",
  lengthError: "",
  widthError: "",
  heightError: "",
  lengthErrorFlag: false,
  widthErrorFlag: false,
  heightErrorFlag: false,

  placeId: null,
  address: "",
  postalCode: "",
  city: "",
  country: "",
  gMapsLink: "",
  coordinates: "",
  contactPhone: "",

  // 🆕 Reference (per point)
  reference: "",

  // ✅ Loading Access
  loadingAccess: null,
  loadingAccessError: false,

  // ✅ Handling Equipment
  handlingEquipment: null,
  handlingEquipmentOther: "",
  handlingEquipmentError: false,
  handlingEquipmentOtherError: false,

  showMoreFields: false,
  // ⬇️ flags de erro por campo
  locationError: false,
  dateError: false,
  hourError: false,
  addressError: false,
  postalCodeError: false,
  cityError: false,
  countryError: false,
  gMapsLinkError: false,
  coordinatesError: false,
  contactPhoneError: false,
});

const normalizePoint = (p = {}) => ({
  ...makeEmptyPoint(),
  ...p,

  location: p.location ?? "",
  date: p.date ?? "",
  hour: p.hour ?? "",
  // 🆕 point-level dimensions
  length: p.length == null ? "" : String(p.length),
  width: p.width == null ? "" : String(p.width),
  height: p.height == null ? "" : String(p.height),
  lengthError: "",
  widthError: "",
  heightError: "",
  lengthErrorFlag: false,
  widthErrorFlag: false,
  heightErrorFlag: false,
  placeId: p.placeId ?? null,
  address: p.address ?? "",
  postalCode: p.postalCode ?? "",
  city: p.city ?? "",
  country: p.country ?? "",
  gMapsLink: p.gMapsLink ?? "",
  coordinates: p.coordinates ?? "",
  contactPhone: p.contactPhone ?? "",
  reference: typeof p.reference === "string" ? p.reference : "",
  loadingAccess: p.loadingAccess ?? null,
  handlingEquipment: p.handlingEquipment ?? null,
  handlingEquipmentOther: p.handlingEquipmentOther ?? "",

  showMoreFields: !!p.showMoreFields,

  locationError: false,
  dateError: false,
  hourError: false,
  addressError: false,
  postalCodeError: false,
  cityError: false,
  countryError: false,
  gMapsLinkError: false,
  coordinatesError: false,
  contactPhoneError: false,
  loadingAccessError: false,
  handlingEquipmentError: false,
  handlingEquipmentOtherError: false,
});

const pickFirstArray = (...candidates) => {
  for (const arr of candidates) {
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const getPrefillPickupPoints = (pf) =>
  pickFirstArray(
    pf?.pickupDeliveries,
    pf?.pickupPoints,
    pf?.pickupLocations
  );

const getPrefillDeliveryPoints = (pf) =>
  pickFirstArray(
    pf?.deliveryDeliveries,
    pf?.deliveryPoints,
    pf?.deliveryLocations
  );

function EditPendentLoadBearer() {
  const { t } = useTranslation("edit_pendentload_bearer");

  const tNonEmpty = (key, fallback) => {
    const v = t(key, { defaultValue: "" });
    return typeof v === "string" && v.trim() ? v : fallback;
  };

  const loadingAccessOptions = [
    { value: "SIDE", label: t("loadingAccess.options.SIDE", { defaultValue: "Side" }) },
    { value: "REAR", label: t("loadingAccess.options.REAR", { defaultValue: "Rear" }) },
    { value: "TOP", label: t("loadingAccess.options.TOP", { defaultValue: "Top" }) },
    { value: "DOCK", label: t("loadingAccess.options.DOCK", { defaultValue: "Dock" }) },
    { value: "ANY", label: t("loadingAccess.options.ANY", { defaultValue: "Any" }) },
  ];

  const handlingEquipmentOptions = [
    { value: "FORKLIFT", label: t("handlingEquipment.options.FORKLIFT", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK", label: t("handlingEquipment.options.PALLET_JACK", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.options.ELECTRIC_PALLET_JACK", { defaultValue: "Electric pallet jack" }), },
    { value: "CRANE", label: t("handlingEquipment.options.CRANE", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP", label: t("handlingEquipment.options.LOADING_RAMP", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT", label: t("handlingEquipment.options.TAIL_LIFT", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD", label: t("handlingEquipment.options.HAND_LOAD", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR", label: t("handlingEquipment.options.CONVEYOR", { defaultValue: "Conveyor" }) },
    { value: "OTHER", label: t("handlingEquipment.options.OTHER", { defaultValue: "Other" }) },
  ];

  const trailerTypeOptions = [
    { value: "ANY", label: t("trailerType.options.ANY", { defaultValue: "Any" }) },
    { value: "TAUTLINER", label: t("trailerType.options.TAUTLINER", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER", label: t("trailerType.options.REEFER", { defaultValue: "Reefer / Frigo" }) },
    { value: "BOX", label: t("trailerType.options.BOX", { defaultValue: "Box" }) },
    { value: "MEGA", label: t("trailerType.options.MEGA", { defaultValue: "Mega" }) },
    { value: "FLATBED", label: t("trailerType.options.FLATBED", { defaultValue: "Flatbed" }) },
    { value: "TIPPER", label: t("trailerType.options.TIPPER", { defaultValue: "Tipper" }) },
    { value: "LOWBED", label: t("trailerType.options.LOWBED", { defaultValue: "Lowbed" }) },
    { value: "TANKER", label: t("trailerType.options.TANKER", { defaultValue: "Tanker" }) },
    { value: "CONTAINER_CHASSIS", label: t("trailerType.options.CONTAINER_CHASSIS", { defaultValue: "Container chassis" }) },
    { value: "OTHER", label: t("trailerType.options.OTHER", { defaultValue: "Other" }) },
  ];

  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads?view=pendent");
  };

  // ---------- READY FLAGS ----------
  const [readyPlaces, setReadyPlaces] = useState(false);
  const [readyCarriers, setReadyCarriers] = useState(false);

  // ---------- OPTIONS ----------
  const [placeOptions, setPlaceOptions] = useState([]);
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [industryOptions, setIndustryOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState(() =>
    Array.isArray(prefill?.clientContactEmails)
      ? prefill.clientContactEmails
          .map((email) => String(email || "").trim().toLowerCase())
          .filter(Boolean)
          .map((email) => ({
            value: email,
            label: email,
          }))
      : []
  );
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState([]);

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
      addEmail(contact?.email);
    });

    addEmail(clientObj.contactEmail);
    addEmail(clientObj.mainContactEmail);
    addEmail(clientObj.secondaryContactEmail);

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  // ---------- MENSAGENS GERAIS ----------
  const [formError, setFormError] = useState("");
  const [clientTypeError, setClientTypeError] = useState("");
  const [clientSelectionError, setClientSelectionError] = useState("");

  // ---------- CORE STATE (pré-populado via prefill) ----------
  const [load, setLoad] = useState(prefill || null); // render imediato
  const [length, setLength] = useState(() => prefill?.length?.toString() || "");
  const [height, setHeight] = useState(() => prefill?.height?.toString() || "");
  const [width, setWidth] = useState(() => prefill?.width?.toString() || "");
  const [weight, setWeight] = useState(() => prefill?.weight?.toString() || "");
  const [price, setPrice] = useState(() =>
    prefill?.price != null ? String(prefill.price) : ""
  );
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (only when external carrier selected)
  const [price4subcontracted, setPrice4subcontracted] = useState(() =>
    prefill?.price4subcontracted != null ? String(prefill.price4subcontracted) : ""
  );
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  const [notes4driver, setNotes4driver] = useState(
    () => prefill?.notes4driver || ""
  );

  // 🆕 Reference
  const [reference, setReference] = useState(() => prefill?.reference || "");

  // 🆕 Merchandise
  const [merchandise, setMerchandise] = useState(() => prefill?.merchandise || "");

  const [merchandiseError, setMerchandiseError] = useState("");
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);

  // 🆕 Trailer Type
  const [trailerType, setTrailerType] = useState(() => prefill?.trailerType || null);
  const [trailerTypeOther, setTrailerTypeOther] = useState(() => prefill?.trailerTypeOther || "");
  const [trailerTypeError, setTrailerTypeError] = useState(false);
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState(false);

  const [clientType, setClientType] = useState(() =>
    prefill?.industryClientId ? "industry" : prefill?.carrierClientId ? "carrier" : ""
  );

  const [selectedClient, setSelectedClient] = useState(() => {
    if (prefill?.industryClientId) {
      return {
        value: prefill.industryClientId,
        label:
          prefill.industryClientName || prefill.industryClient?.companyName || "",
      };
    }
    if (prefill?.carrierClientId) {
      return {
        value: prefill.carrierClientId,
        label:
          prefill.carrierClientName || prefill.carrierClient?.companyName || "",
      };
    }
    return null;
  });

  const [pickupPoints, setPickupPoints] = useState(() => {
    const src = getPrefillPickupPoints(prefill).slice(0, MAX_POINTS);
    return src.length ? src.map(normalizePoint) : [makeEmptyPoint()];
  });

  const [deliveryPoints, setDeliveryPoints] = useState(() => {
    const src = getPrefillDeliveryPoints(prefill).slice(0, MAX_POINTS);
    return src.length ? src.map(normalizePoint) : [makeEmptyPoint()];
  });

  const [ready, setReady] = useState(() => !!prefill);

  // Transport carrier (obj para Select)
  const [transportCarrierId, setTransportCarrierId] = useState(() =>
    prefill?.carrierId
      ? {
          value: String(prefill.carrierId),
          label:
            prefill.carrierName ||
            prefill.loadsAsCarrier?.companyName ||
            t("general.carrierFallback"),
          isInternal: false,
        }
      : prefill
      ? {
          value: "__internal__",
          label: prefill.carrierName || t("general.internalCarrier", { defaultValue: "Internal carrier" }),
          isInternal: true,
        }
      : null
  );
  const [carrierError, setCarrierError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  // ---------- ERRORS ----------
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [weightError, setWeightError] = useState("");
  const inputRefs = useRef({});

  const closeAllNativePickers = (exceptKey = null) => {
    Object.entries(inputRefs.current).forEach(([key, el]) => {
      if (!el || key === exceptKey) return;

      const type = el.getAttribute?.("type");
      if (type === "date") {
        try {
          el.blur();
        } catch (err) {
          // ignore
        }
      }
    });
  };

  // ---------- INJEÇÃO “ON-LOAD” DOS CLIENTES ----------
  const clientOptionsReady = useMemo(
    () => (clientType === "industry" ? !!industryOptions.length : !!carrierOptions.length),
    [clientType, industryOptions.length, carrierOptions.length]
  );

  const baseClientOptions = useMemo(
    () => (clientType === "industry" ? industryOptions : carrierOptions),
    [clientType, industryOptions, carrierOptions]
  );

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType || !selectedClient?.value) return [];

    const sourceList = clientType === "industry" ? industryOptions : carrierOptions;
    const fullClient = (sourceList || []).find(
      (c) => String(c.value) === String(selectedClient.value)
    );

    return extractClientEmails(fullClient?.raw || null);
  }, [clientType, selectedClient, industryOptions, carrierOptions]);

  const injectedClientOptions = useMemo(() => {
    if (!clientOptionsReady && selectedClient) {
      const exists = baseClientOptions.some((o) => o.value === selectedClient.value);
      return exists ? baseClientOptions : [selectedClient, ...baseClientOptions];
    }
    return baseClientOptions;
  }, [clientOptionsReady, selectedClient, baseClientOptions]);

  const clientIsLoading = useMemo(
    () => !clientOptionsReady && !selectedClient,
    [clientOptionsReady, selectedClient]
  );
  const clientMenuIsOpen = useMemo(
    () => (clientOptionsReady ? undefined : false),
    [clientOptionsReady]
  );

  // ---------- INJEÇÃO “ON-LOAD” DO CARRIER ----------
  const injectedCarrierOptions = useMemo(() => {
    if (!readyCarriers && transportCarrierId) {
      const exists = availableCarriers.some(
        (o) => String(o.value) === String(transportCarrierId.value)
      );
      return exists ? availableCarriers : [transportCarrierId, ...availableCarriers];
    }
    return availableCarriers;
  }, [readyCarriers, transportCarrierId, availableCarriers]);

  const carrierIsLoading = useMemo(
    () => !readyCarriers && !transportCarrierId,
    [readyCarriers, transportCarrierId]
  );
  const carrierMenuIsOpen = useMemo(() => (readyCarriers ? undefined : false), [readyCarriers]);

  const selectedTransportOptMemo = useMemo(() => {
    return (
      (availableCarriers || []).find(
        (c) => String(c.value) === String(transportCarrierId?.value)
      ) || transportCarrierId || null
    );
  }, [availableCarriers, transportCarrierId]);

  const isInternalCarrierMemo = useMemo(() => {
    return (
      selectedTransportOptMemo?.isInternal ||
      String(selectedTransportOptMemo?.value) === "__internal__"
    );
  }, [selectedTransportOptMemo]);

  const showPrice4subcontracted = useMemo(() => {
    if (!transportCarrierId?.value) return false;
    return !isInternalCarrierMemo;
  }, [transportCarrierId, isInternalCarrierMemo]);

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";

    if (!Number.isFinite(n) || n <= 0) {
      if (field === "length") return t("validation.lengthRequired");
      if (field === "width") return t("validation.widthRequired");
      if (field === "height") return t("validation.heightRequired");
      return t("validation.fieldRequired", { defaultValue: "❌ Campo obrigatório." });
    }

    if (field === "length" && n > MAX_LENGTH) {
      return t("validation.lengthMax", { max: MAX_LENGTH.toFixed(2) });
    }

    if (field === "width" && n > MAX_WIDTH) {
      return t("validation.widthMax", { max: MAX_WIDTH.toFixed(2) });
    }

    if (field === "height" && n > MAX_HEIGHT) {
      return t("validation.heightMax", { max: MAX_HEIGHT.toFixed(2) });
    }

    return "";
  };

  const handlePointDimensionChange = (index, field, value, isPickup) => {
    const dimensionError = getPointDimensionError(field, value);

    const apply = (prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
        [`${field}Error`]: dimensionError,
        [`${field}ErrorFlag`]: !!dimensionError,
      };

      return updated;
    };

    if (isPickup) {
      setPickupPoints(apply);
    } else {
      setDeliveryPoints(apply);
    }

    if (formError) setFormError("");
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickupPoints);
  }, [pickupPoints]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPoints);
  }, [deliveryPoints]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches:
        Math.round(pickupLength * 100) === Math.round(deliveryLength * 100),
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength =
      pointDimensionSummary.length > 0
        ? pointDimensionSummary.length.toFixed(2)
        : "";

    const nextWidth =
      pointDimensionSummary.width > 0
        ? pointDimensionSummary.width.toFixed(2)
        : "";

    const nextHeight =
      pointDimensionSummary.height > 0
        ? pointDimensionSummary.height.toFixed(2)
        : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showPrice4subcontracted || !transportCarrierId?.value) return [];

    const selectedCarrier = (carrierOptions || []).find(
      (c) => String(c.value) === String(transportCarrierId.value)
    );

    return extractClientEmails(selectedCarrier?.raw || null);
  }, [showPrice4subcontracted, transportCarrierId, carrierOptions]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ chips numa só linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal em vez de aumentar altura
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para X + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCompanyName = async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (cancelled) return;
        setCompanyName(String(res.data?.company_name || "").trim());
      } catch (err) {
        console.warn("❌ Error fetching company name:", err?.response?.status);
        if (!cancelled) setCompanyName("");
      }
    };

    fetchCompanyName();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- FETCH “pesado” EM BACKGROUND ----------
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        // 1) Load (singular)
        axios.get(`/pendentloads/${loadId}`).then((res) => {
          if (cancelled) return;

          const d = res.data || {};
          const apiPickups = (d.pickupDeliveries || []).slice(0, MAX_POINTS).map(normalizePoint);
          const apiDeliveries = (d.deliveryDeliveries || []).slice(0, MAX_POINTS).map(normalizePoint);

          if (!prefill) {
            setLoad(d);
            setLength(d.length != null ? String(d.length) : "");
            setHeight(d.height != null ? String(d.height) : "");
            setWidth(d.width != null ? String(d.width) : "");
            setWeight(d.weight != null ? String(d.weight) : "");
            setMerchandise(typeof d.merchandise === "string" ? d.merchandise : "");
            setPrice(d.price != null ? String(d.price) : "");
            setPrice4subcontracted(
              d.price4subcontracted != null ? String(d.price4subcontracted) : ""
            );
            setSelectedClientContactEmails(
              Array.isArray(d.clientContactEmails)
                ? d.clientContactEmails
                    .map((email) => String(email || "").trim().toLowerCase())
                    .filter(Boolean)
                    .map((email) => ({
                      value: email,
                      label: email,
                    }))
                : []
            );

            setSelectedSubcontractedEmails(
              Array.isArray(d.subcontractedEmails)
                ? d.subcontractedEmails
                    .map((email) => String(email || "").trim().toLowerCase())
                    .filter(Boolean)
                    .map((email) => ({
                      value: email,
                      label: email,
                    }))
                : []
            );
            setReference(typeof d.reference === "string" ? d.reference : "");
            setNotes4driver(typeof d.notes4driver === "string" ? d.notes4driver : "");
            setTrailerType(d.trailerType ?? null);
            setTrailerTypeOther(typeof d.trailerTypeOther === "string" ? d.trailerTypeOther : "");

            setPickupPoints(apiPickups.length ? apiPickups : [makeEmptyPoint()]);
            setDeliveryPoints(apiDeliveries.length ? apiDeliveries : [makeEmptyPoint()]);

            if (d.industryClientId) {
              setClientType("industry");
              setSelectedClient({
                value: String(d.industryClientId),
                label: d.industryClientName || d.industryClient?.companyName || "",
              });
            } else if (d.carrierClientId) {
              setClientType("carrier");
              setSelectedClient({
                value: String(d.carrierClientId),
                label: d.carrierClientName || d.carrierClient?.companyName || "",
              });
            } else {
              setClientType("");
              setSelectedClient(null);
            }

            if (d.carrierId) {
              setTransportCarrierId({
                value: String(d.carrierId),
                label: d.carrierName || d.loadsAsCarrier?.companyName || t("general.carrierFallback"),
                isInternal: false,
              });
            } else {
              setTransportCarrierId({
                value: "__internal__",
                label: companyName
                  ? `${companyName} (internal)`
                  : t("general.internalCarrier", { defaultValue: "Internal carrier" }),
                isInternal: true,
              });
            }
          } else {
            setLoad((prev) => prev || d);

            setLength((prev) => (String(prev || "").trim() ? prev : (d.length != null ? String(d.length) : "")));
            setHeight((prev) => (String(prev || "").trim() ? prev : (d.height != null ? String(d.height) : "")));
            setWidth((prev) => (String(prev || "").trim() ? prev : (d.width != null ? String(d.width) : "")));
            setWeight((prev) => (String(prev || "").trim() ? prev : (d.weight != null ? String(d.weight) : "")));
            setPrice((prev) => (String(prev || "").trim() ? prev : (d.price != null ? String(d.price) : "")));
            setPrice4subcontracted((prev) =>
              String(prev || "").trim() ? prev : (d.price4subcontracted != null ? String(d.price4subcontracted) : "")
            );
            setSelectedClientContactEmails((prev) => {
              if (Array.isArray(prev) && prev.length > 0) return prev;

              return Array.isArray(d.clientContactEmails)
                ? d.clientContactEmails
                    .map((email) => String(email || "").trim().toLowerCase())
                    .filter(Boolean)
                    .map((email) => ({
                      value: email,
                      label: email,
                    }))
                : [];
            });

            setSelectedSubcontractedEmails((prev) => {
              if (Array.isArray(prev) && prev.length > 0) return prev;

              return Array.isArray(d.subcontractedEmails)
                ? d.subcontractedEmails
                    .map((email) => String(email || "").trim().toLowerCase())
                    .filter(Boolean)
                    .map((email) => ({
                      value: email,
                      label: email,
                    }))
                : [];
            });
            setReference((prev) =>
              String(prev || "").trim() ? prev : (typeof d.reference === "string" ? d.reference : "")
            );
            setNotes4driver((prev) =>
              String(prev || "").trim() ? prev : (typeof d.notes4driver === "string" ? d.notes4driver : "")
            );
            setMerchandise((prev) =>
              String(prev || "").trim() ? prev : (typeof d.merchandise === "string" ? d.merchandise : "")
            );
            setTrailerType((prev) => (prev ? prev : (d.trailerType ?? null)));
            setTrailerTypeOther((prev) =>
              String(prev || "").trim() ? prev : (typeof d.trailerTypeOther === "string" ? d.trailerTypeOther : "")
            );

            setPickupPoints((prev) => {
              if (!prev?.length) return apiPickups.length ? apiPickups : [makeEmptyPoint()];
              return prev.map((p, i) => ({ ...(apiPickups[i] || makeEmptyPoint()), ...p }));
            });

            setDeliveryPoints((prev) => {
              if (!prev?.length) return apiDeliveries.length ? apiDeliveries : [makeEmptyPoint()];
              return prev.map((p, i) => ({ ...(apiDeliveries[i] || makeEmptyPoint()), ...p }));
            });
          }

          setReady(true);
        });

        // 2) Clients
        axios.get("/industries").then((res) => {
          if (cancelled) return;
          const list = res.data
            .map((i) => ({ value: i.id, label: i.companyName, raw: i }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
          setIndustryOptions(list);
        });

        axios.get("/carriers").then((res) => {
          if (cancelled) return;

          const getCarrierName = (c) =>
            String(
              c?.companyName ||
              c?.name ||
              c?.company?.companyName ||
              c?.company?.name ||
              ""
            ).trim();

          const externalList = (res.data || [])
            .map((c) => ({
              value: String(c.id),
              label: getCarrierName(c),
              isInternal: false,
              searchText: getCarrierName(c).toLowerCase(),
              raw: c,
            }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
            );

          const internalOption =
            companyName && String(companyName).trim()
              ? {
                  value: "__internal__",
                  label: `${String(companyName).trim()} (internal)`,
                  isInternal: true,
                  searchText: `${String(companyName).trim()} internal`.toLowerCase(),
                }
              : {
                  value: "__internal__",
                  label: t("general.internalCarrier", { defaultValue: "Internal carrier" }),
                  isInternal: true,
                  searchText: "internal carrier",
                };

          const finalList = [internalOption, ...externalList];

          setCarrierOptions(externalList);
          setAvailableCarriers(finalList);
          setReadyCarriers(true);
        });

        // 3) Places
        axios.get("/places").then((res) => {
          if (cancelled) return;
          const list = res.data.map((p) => ({ label: p.placeName, value: p.id, fullData: p }));
          setPlaceOptions(list);
          setReadyPlaces(true);
        });
      } catch (e) {
        console.error("❌ Background fetch error", e);
        setReady(true);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [loadId, t, companyName]);

  useEffect(() => {
    if (!availableCarriers?.length) return;

    const internalOpt = availableCarriers.find(
      (c) => c?.isInternal || String(c?.value) === "__internal__"
    );

    if (!internalOpt) return;

    if (!transportCarrierId?.value) {
      setTransportCarrierId(internalOpt);
      return;
    }

    if (String(transportCarrierId.value) === "__internal__") {
      setTransportCarrierId(internalOpt);
    }
  }, [availableCarriers, transportCarrierId]);

    useEffect(() => {
      if (!showPrice4subcontracted) {
        setPrice4subcontracted("");
        setPrice4subcontractedError("");
        setSelectedSubcontractedEmails([]);
      }
    }, [showPrice4subcontracted]);

  // ---------- VALIDATORS ----------
  const handleLengthChange = (e) => {
    const val = e.target.value;
    setLength(val);

    if (!isNonEmpty(val)) {
      setLengthError("");
      return;
    }

    const num = Number(val);

    if (!Number.isFinite(num) || num <= 0) {
      setLengthError(t("validation.lengthMin", { min: MIN_LENGTH.toFixed(2) }));
    } else if (num > MAX_LENGTH) {
      setLengthError(t("validation.lengthMax", { max: MAX_LENGTH.toFixed(2) }));
    } else {
      setLengthError("");
    }
  };

  const handleHeightChange = (e) => {
    const val = e.target.value;
    setHeight(val);

    if (!isNonEmpty(val)) {
      setHeightError("");
      return;
    }

    const num = Number(val);

    if (!Number.isFinite(num) || num <= 0) {
      setHeightError(t("validation.heightMin", { min: "0.01" }));
    } else if (num > MAX_HEIGHT) {
      setHeightError(t("validation.heightMax", { max: MAX_HEIGHT.toFixed(2) }));
    } else {
      setHeightError("");
    }
  };

  const handleWidthChange = (e) => {
    const val = e.target.value;
    setWidth(val);

    if (!isNonEmpty(val)) {
      setWidthError("");
      return;
    }

    const num = Number(val);

    if (!Number.isFinite(num) || num <= 0) {
      setWidthError(t("validation.widthMin", { min: "0.01" }));
    } else if (num > MAX_WIDTH) {
      setWidthError(t("validation.widthMax", { max: MAX_WIDTH.toFixed(2) }));
    } else {
      setWidthError("");
    }
  };

  const handleWeightChange = (e) => {
    const val = e.target.value;
    setWeight(val);

    if (!isNonEmpty(val)) {
      setWeightError("");
      return;
    }

    const num = Number(val);

    if (!Number.isFinite(num) || num <= 0) {
      setWeightError(t("validation.weightMin", { min: 0.01 }));
    } else {
      setWeightError("");
    }
  };

  // Mantém só os campos que o backend espera (sem *Error, sem showMoreFields)
  const sanitizePoints = (points) =>
    points.map((p) => {
      const he = String(p.handlingEquipment || "").trim().toUpperCase();
      const heOther =
        he === "OTHER"
          ? String(p.handlingEquipmentOther || "").trim()
          : "";

      return {
        placeId: p.placeId ?? null,
        location: String(p.location || "").trim(),
        date: String(p.date || "").trim() || null,
        hour: String(p.hour || "").trim() || null,

        // 🆕 point-level dimensions
        length: p.length === "" || p.length == null ? null : Number(p.length),
        width: p.width === "" || p.width == null ? null : Number(p.width),
        height: p.height === "" || p.height == null ? null : Number(p.height),

        address: String(p.address || "").trim() || null,
        postalCode: String(p.postalCode || "").trim() || null,
        city: String(p.city || "").trim() || null,
        country: String(p.country || "").trim() || null,
        gMapsLink: String(p.gMapsLink || "").trim() || null,
        coordinates: String(p.coordinates || "").trim() || null,
        contactPhone: String(p.contactPhone || "").trim() || null,

        // 🆕 Reference (per point)
        reference: String(p.reference || "").trim() || null,

        loadingAccess:
          String(p.loadingAccess || "").trim().toUpperCase() || null,

        // ✅ Handling Equipment
        handlingEquipment: he || null,
        handlingEquipmentOther: he === "OTHER" && heOther ? heOther : null,
      };
    });

  // Mensagem geral quando há bloqueios de validação
  const composeFormError = ({
    merchInvalid,
    trailerInvalid,
    hasPointErrors,
  }) => {
    const parts = [];

    if (merchInvalid) {
      parts.push(t("errors.parts.merchandise"));
    }

    if (trailerInvalid) {
      parts.push(
        t("errors.parts.trailerType", {
          defaultValue: "Tipo de reboque",
        })
      );
    }

    if (hasPointErrors) {
      parts.push(t("errors.parts.points"));
    }

    return t("errors.fixFollowing", { fields: parts.join(" • ") });
  };

  const removePickUpPlace = (i) => {
    setPickupPoints((prev) => {
      if (prev.length <= 1) return prev; // mantém pelo menos 1
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [makeEmptyPoint()];
    });
    if (formError) setFormError("");
  };

  const removeDeliveryPlace = (i) => {
    setDeliveryPoints((prev) => {
      if (prev.length <= 1) return prev; // mantém pelo menos 1
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [makeEmptyPoint()];
    });
    if (formError) setFormError("");
  };

  // Render de uma opção no menu
  const PlaceOption = (props) => {
    const { data } = props;
    const city = data.fullData?.city || "";
    const country = data.fullData?.country || "";
    const hasMeta = city || country;

    return (
      <RSComponents.Option {...props}>
        <div className="place-opt">
          <div className="place-opt-line1">{data.label}</div>
          {hasMeta && (
            <div className="place-opt-line2">{[city, country].filter(Boolean).join(", ")}</div>
          )}
        </div>
      </RSComponents.Option>
    );
  };

  // Render do valor selecionado no input
  const PlaceSingleValue = (props) => {
    const { data } = props;
    const city = data.fullData?.city || "";
    const country = data.fullData?.country || "";
    const hasMeta = city || country;

    return (
      <RSComponents.SingleValue {...props}>
        <div className="place-sel">
          <div className="place-sel-line1">{data.label}</div>
          {hasMeta && (
            <div className="place-sel-line2">{[city, country].filter(Boolean).join(", ")}</div>
          )}
        </div>
      </RSComponents.SingleValue>
    );
  };

  // ---------- SAVE ----------
  const handleSaveChanges = async (e) => {
    e.preventDefault();

    setHasTriedSubmit(true);
    setFormError("");
    setClientTypeError("");
    setClientSelectionError("");
    setCarrierError(false);
    setTrailerTypeError(false);
    setTrailerTypeOtherError(false);

    let block = false;

    const hasLength = isNonEmpty(length);
    const hasHeight = isNonEmpty(height);
    const hasWidth = isNonEmpty(width);
    const hasWeight = isNonEmpty(weight);

    const len = hasLength ? Number(length) : null;
    const hei = hasHeight ? Number(height) : null;
    const wid = hasWidth ? Number(width) : null;
    const wei = hasWeight ? Number(weight) : null;

    const nextLengthError =
      hasLength && (!Number.isFinite(len) || len <= 0)
        ? t("validation.lengthMin", { min: MIN_LENGTH.toFixed(2) })
        : hasLength && len > MAX_LENGTH
        ? t("validation.lengthMax", { max: MAX_LENGTH.toFixed(2) })
        : "";

    const nextHeightError =
      hasHeight && (!Number.isFinite(hei) || hei <= 0)
        ? t("validation.heightMin", { min: "0.01" })
        : hasHeight && hei > MAX_HEIGHT
        ? t("validation.heightMax", { max: MAX_HEIGHT.toFixed(2) })
        : "";

    const nextWidthError =
      hasWidth && (!Number.isFinite(wid) || wid <= 0)
        ? t("validation.widthMin", { min: "0.01" })
        : hasWidth && wid > MAX_WIDTH
        ? t("validation.widthMax", { max: MAX_WIDTH.toFixed(2) })
        : "";

    const nextWeightError =
      hasWeight && (!Number.isFinite(wei) || wei <= 0)
        ? t("validation.weightMin", { min: 0.01 })
        : "";

    setLengthError(nextLengthError);
    setHeightError(nextHeightError);
    setWidthError(nextWidthError);
    setWeightError(nextWeightError);

    const hasDimensionOrWeightError = !!(
      nextLengthError ||
      nextHeightError ||
      nextWidthError ||
      nextWeightError
    );

    if (hasDimensionOrWeightError) {
      block = true;
    }

    let hasPriceErr = false;
    let parsedPrice = null;

    if (price != null && String(price).trim() !== "") {
      const n = Number(price);

      if (!Number.isFinite(n) || n < 0) {
        setPriceError(t("validation.invalidPrice"));
        hasPriceErr = true;
        block = true;
      } else {
        parsedPrice = Number(n.toFixed(2));
        setPriceError("");
      }
    } else {
      setPriceError("");
    }

    let hasPrice4subcontractedErr = false;
    let parsedPrice4subcontracted = null;

    if (
      showPrice4subcontracted &&
      price4subcontracted != null &&
      String(price4subcontracted).trim() !== ""
    ) {
      const n = Number(price4subcontracted);

      if (!Number.isFinite(n) || n < 0) {
        setPrice4subcontractedError(t("validation.invalidPrice"));
        hasPrice4subcontractedErr = true;
        block = true;
      } else {
        parsedPrice4subcontracted = Number(n.toFixed(2));
        setPrice4subcontractedError("");
      }
    } else {
      setPrice4subcontractedError("");
    }

    const merchText = (merchandise || "").trim();

    if (!merchText) {
      setMerchandiseError(
        t("validation.merchandiseRequired", {
          defaultValue: "❌ A mercadoria é obrigatória.",
        })
      );
      block = true;
    } else if (merchText.length > 5000) {
      setMerchandiseError(t("validation.merchandiseTooLong"));
      block = true;
    } else {
      setMerchandiseError("");
    }

    const tt = String(trailerType || "").trim().toUpperCase();
    const missingTrailerOther =
      tt === "OTHER" && !isNonEmpty(trailerTypeOther);

    setTrailerTypeError(false);
    setTrailerTypeOtherError(missingTrailerOther);

    if (missingTrailerOther) {
      block = true;
    }

    const markPointErrors = (p) => {
      const next = {
        ...p,
        locationError: !isNonEmpty(p.location),
        dateError: false,
        hourError: false,
        addressError: false,
        postalCodeError: false,
        cityError: false,
        countryError: false,
        gMapsLinkError: false,
        coordinatesError: false,
        contactPhoneError: false,
        loadingAccessError: false,
        handlingEquipmentError: false,
      };

      ["length", "width", "height"].forEach((field) => {
        const dimensionError = getPointDimensionError(field, p[field]);

        next[`${field}Error`] = dimensionError;
        next[`${field}ErrorFlag`] = !!dimensionError;
      });

      const handlingEquipment = String(
        next.handlingEquipment || ""
      ).trim().toUpperCase();

      next.handlingEquipmentOtherError =
        handlingEquipment === "OTHER" &&
        !isNonEmpty(next.handlingEquipmentOther);

      next.showMoreFields =
        next.showMoreFields ||
        next.handlingEquipmentOtherError;

      return next;
    };

    let hasPointErrors = false;

    const nextPickups = pickupPoints.map((p) => {
      const nextPoint = markPointErrors(p);

      if (!pointIsComplete(nextPoint)) {
        hasPointErrors = true;
      }

      return nextPoint;
    });

    const nextDeliveries = deliveryPoints.map((p) => {
      const nextPoint = markPointErrors(p);

      if (!pointIsComplete(nextPoint)) {
        hasPointErrors = true;
      }

      return nextPoint;
    });

    nextPickups.forEach((p) => {
      if (
        isNonEmpty(p.hour) &&
        !/^([01]\d|2[0-3]):([0-5]\d)$/.test(String(p.hour).trim())
      ) {
        p.hourError = true;
        hasPointErrors = true;
      }
    });

    nextDeliveries.forEach((p) => {
      if (
        isNonEmpty(p.hour) &&
        !/^([01]\d|2[0-3]):([0-5]\d)$/.test(String(p.hour).trim())
      ) {
        p.hourError = true;
        hasPointErrors = true;
      }
    });

    setPickupPoints(nextPickups);
    setDeliveryPoints(nextDeliveries);

    if (hasPointErrors) {
      block = true;
    }

    if (block) {
      setFormError(
        composeFormError({
          merchInvalid:
            hasDimensionOrWeightError ||
            hasPriceErr ||
            hasPrice4subcontractedErr ||
            !merchText ||
            merchText.length > 5000,
          trailerInvalid: missingTrailerOther,
          hasPointErrors,
        })
      );
      return;
    }

    const hasTransportCarrier =
      !!String(transportCarrierId?.value || "").trim();

    const isInternalCarrier =
      hasTransportCarrier &&
      (
        transportCarrierId?.isInternal ||
        String(transportCarrierId?.value) === "__internal__"
      );

    const payload = {
      length: len,
      height: hei,
      width: wid,
      weight: wei,

      reference:
        reference && reference.trim().length > 0
          ? reference.trim()
          : null,

      trailerType: tt || null,
      trailerTypeOther:
        tt === "OTHER"
          ? (trailerTypeOther || "").trim() || null
          : null,

      merchandise: merchText,

      price: parsedPrice,
      price4subcontracted: parsedPrice4subcontracted,
      notes4driver:
        notes4driver && notes4driver.trim().length > 0
          ? notes4driver.trim()
          : null,
      industryClientId:
        clientType === "industry" ? selectedClient?.value ?? null : null,
      carrierClientId:
        clientType === "carrier" ? selectedClient?.value ?? null : null,
      clientContactEmails: selectedClientContactEmails
        .map((opt) => String(opt?.value || "").trim().toLowerCase())
        .filter(Boolean),
      subcontractedEmails:
        !hasTransportCarrier || isInternalCarrier
          ? []
          : selectedSubcontractedEmails
              .map((opt) => String(opt?.value || "").trim().toLowerCase())
              .filter(Boolean),
      carrierId:
        !hasTransportCarrier || isInternalCarrier
          ? null
          : Number(transportCarrierId?.value),
      carrierName:
        !hasTransportCarrier
          ? null
          : isInternalCarrier
          ? companyName || null
          : transportCarrierId?.label || null,
      pickupPoints: sanitizePoints(nextPickups),
      deliveryPoints: sanitizePoints(nextDeliveries),
      loadBearer: true,
    };

    try {
      setLoading(true);
      await axios.put(`/pendentloads/${loadId}`, payload);
      setSuccessPopupOpen(true);
    } catch (err) {
      const backendError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("validation.updateFailed");

      console.error("❌ Error updating pendent load:", {
        status: err?.response?.status,
        data: err?.response?.data,
        error: err,
      });

      setFormError(backendError);
    } finally {
      setLoading(false);
    }
  };

  const allPointsComplete = (points) => points.every(pointIsComplete);

  const formLooksValid = () => {
    const dimensionsAndWeightOk =
      !lengthError &&
      !heightError &&
      !widthError &&
      !weightError;

    const pricesOk =
      !priceError &&
      !price4subcontractedError;

    const tt = String(trailerType || "").trim().toUpperCase();
    const trailerOk =
      tt !== "OTHER" || isNonEmpty(trailerTypeOther);

    const pickupsOk = allPointsComplete(pickupPoints);
    const deliveriesOk = allPointsComplete(deliveryPoints);

    const merchandiseOk =
      isNonEmpty(merchandise) &&
      !merchandiseError;

    return (
      dimensionsAndWeightOk &&
      pricesOk &&
      merchandiseOk &&
      trailerOk &&
      pickupsOk &&
      deliveriesOk
    );
  };

  useEffect(() => {
    if (formError && formLooksValid()) {
      setFormError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formError,
    clientType,
    selectedClient,
    transportCarrierId,
    length,
    height,
    width,
    weight,
    lengthError,
    heightError,
    widthError,
    weightError,
    pickupPoints,
    deliveryPoints,
    merchandise,
    merchandiseError,
    trailerType,
    trailerTypeOther,
    trailerTypeError,
    trailerTypeOtherError,
  ]);

  if (!ready) return null;
  
  return (
    <div className="edit-pendent-load-bearer-container">
      <div
        className="edit-pendent-load-bearer-back-arrow"
        onClick={() => navigate("/loads?view=pendent")}
      >
        {t("back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-pendent-load-bearer-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-pendent-load-bearer-success-title"
        >
          <div className="edit-pendent-load-bearer-popupModal">
            <div className="edit-pendent-load-bearer-popupIconWrap">
              <FaCheckCircle className="edit-pendent-load-bearer-popupIcon" />
            </div>

            <h2
              id="edit-pendent-load-bearer-success-title"
              className="edit-pendent-load-bearer-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-pendent-load-bearer-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-pendent-load-bearer-popupActions">
              <button
                type="button"
                className="edit-pendent-load-bearer-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-pendent-load-bearer-title">{t("title")}</h1>

      {/* ⬇️ cartão com a borda exterior  */}
      <div className="edit-pendent-load-bearer-card">
        <form
          className="edit-pendent-load-bearer-form"
          onSubmit={handleSaveChanges}
          onFocusCapture={(e) => {
            const target = e.target;
            if (!target) return;

            const currentKey = Object.keys(inputRefs.current).find(
              (key) => inputRefs.current[key] === target
            );

            closeAllNativePickers(currentKey);
          }}
        >
          <div className="edit-pendent-load-bearer-columns">
            {/* COL 1: PICKUPS */}
            <div className="edit-pendent-load-bearer-column">
              <h3 className="edit-pendent-load-bearer-column-title">{t("columns.loadingPoints")}</h3>

              {pickupPoints.map((point, index) => {
                const prefillOption =
                  point.location || point.placeId
                    ? {
                        value: point.placeId ?? `prefill-pickup-${index}-${point.location}`,
                        label: point.location || "",
                      }
                    : null;

                const matched = point.placeId
                  ? placeOptions.find((p) => p.value === point.placeId) || null
                  : null;

                const selectValue = matched || prefillOption || null;
                const selectOptions = readyPlaces
                  ? placeOptions
                  : prefillOption
                  ? [prefillOption, ...placeOptions]
                  : placeOptions;

                return (
                  <div key={index} className="edit-pendent-load-bearer-point-group">
                    <div className="edit-pendent-load-bearer-field-group">
                    {/* Label + Delete */}
                    <div className="edit-pendent-load-label-row">
                      <label
                        className="edit-pendent-load-points-label"
                        htmlFor={`pickup-place-${index}`}
                      >
                        {t("labels.pickupPlace")}
                      </label>

                      {pickupPoints.length > 1 && (
                        <button
                          type="button"
                          className="edit-pendent-load-delete-btn"
                          onClick={() => removePickUpPlace(index)}
                          title={t("titles.removePickup")}
                          aria-label={t("aria.removePickup", { num: index + 1 })}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <CreatableSelect
                      inputId={`pickup-place-${index}`}
                      isClearable
                      isMulti={false}
                      options={selectOptions}
                      isLoading={false}
                      isDisabled={false}
                      menuIsOpen={readyPlaces ? undefined : false}
                      placeholder={t("place.placeholder")}
                      value={selectValue}
                      styles={customSelectStyles}
                      classNamePrefix={
                        point.locationError ? "select-error" : "edit-pendent-load-bearer-select"
                      }
                      onChange={(selectedOption) => {
                        const updated = [...pickupPoints];

                        const clearAllFieldErrors = (pt) => {
                          pt.locationError = false;
                          pt.dateError = false;
                          pt.hourError = false;
                          pt.addressError = false;
                          pt.postalCodeError = false;
                          pt.cityError = false;
                          pt.countryError = false;
                          pt.gMapsLinkError = false;
                          pt.coordinatesError = false;
                          pt.contactPhoneError = false;
                          pt.loadingAccessError = false;

                          // ✅ Handling Equipment
                          pt.handlingEquipmentOtherError = false;
                        };

                        let next = { ...updated[index] };

                        if (!selectedOption) {
                          next = {
                            ...next,
                            placeId: null,
                            location: "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",

                            // ✅ NEW
                            loadingAccess: null,
                          };
                          clearAllFieldErrors(next);
                          next.showMoreFields = false;
                          updated[index] = next;
                          setPickupPoints(updated);
                          if (formError) setFormError("");
                          return;
                        }

                        if (selectedOption.__isNew__) {
                          next = {
                            ...next,
                            placeId: null,
                            location: selectedOption.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                          };
                          clearAllFieldErrors(next);
                        } else if (selectedOption.fullData) {
                          const p = selectedOption.fullData;
                          next = {
                            ...next,
                            placeId: p.id,
                            location: p.placeName || "",
                            address: p.address || "",
                            postalCode: p.postalCode || "",
                            city: p.city || "",
                            country: p.country || "",
                            gMapsLink: p.gMapsLink || "",
                            coordinates: p.coordinates || "",
                            contactPhone: p.contactPhone || "",

                            // ✅ NEW
                            loadingAccess: p.loadingAccess ?? null,
                          };
                          clearAllFieldErrors(next);
                        } else {
                          next = {
                            ...next,
                            placeId: null,
                            location: selectedOption.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null
                          };
                          clearAllFieldErrors(next);
                        }

                        next.showMoreFields = hasAnyHiddenEmpty(next);

                        updated[index] = next;
                        setPickupPoints(updated);
                        if (formError) setFormError("");
                      }}
                      components={{ Option: PlaceOption, SingleValue: PlaceSingleValue }}
                    />

                    {point.locationError && (
                      <p className="edit-pendent-load-bearer-error-location">
                        {t("validation.locationRequired")}
                      </p>
                    )}

                    <div className="edit-pendent-load-bearer-datetime-group">
                      <div className="edit-pendent-load-bearer-input-wrapper">
                        <input
                          type="date"
                          value={point.date || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].date = e.target.value;
                            updated[index].dateError = false;
                            setPickupPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.dateError ? "input-error" : ""
                          }`}
                          ref={(el) => (inputRefs.current[`pickup-date-${index}`] = el)}
                        />
                        <FaCalendarAlt
                          className="edit-pendent-load-bearer-input-icon"
                          onClick={() => {
                            closeAllNativePickers(`pickup-date-${index}`);
                            inputRefs.current[`pickup-date-${index}`]?.focus();
                            inputRefs.current[`pickup-date-${index}`]?.showPicker?.();
                          }}
                        />
                      </div>

                      <div className="edit-pendent-load-bearer-input-wrapper">
                        <input
                          type="text"
                          placeholder="HH:MM"
                          value={point.hour || ""}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);

                            let formatted = raw;
                            if (raw.length >= 3) {
                              formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                            }

                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            const isValid = regex.test(formatted);

                            const updated = [...pickupPoints];
                            updated[index].hour = formatted;
                            updated[index].hourError =
                              formatted.length === 5 && !isValid;

                            setPickupPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.hourError ? "input-error" : ""
                          }`}
                          ref={(el) => (inputRefs.current[`pickup-hour-${index}`] = el)}
                        />
                        <FaClock
                          className="edit-pendent-load-bearer-input-icon"
                          onClick={() => {
                            inputRefs.current[`pickup-hour-${index}`]?.focus();
                          }}
                        />
                      </div>
                    </div>

                    {point.dateError && (
                      <p className="edit-pendent-load-bearer-error">{t("validation.dateRequired")}</p>
                    )}
                    {point.hourError && (
                      <p className="edit-pendent-load-bearer-error">
                        {isNonEmpty(point.hour)
                          ? t("validation.hourInvalid", { defaultValue: "Invalid hour format." })
                          : t("validation.hourRequired")}
                      </p>
                    )}

                    <div className="edit-pendent-load-bearer-point-dimensions-block">
                      <label className="edit-pendent-load-bearer-label edit-pendent-load-bearer-point-dimensions-title">
                        {t("labels.pointDimensions", {
                          defaultValue: "Dimensões da carga neste ponto (em metros)",
                        })}*
                      </label>

                      <div className="edit-pendent-load-bearer-point-dimensions-row">
                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointLengthShort", { defaultValue: "Comprimento" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.length || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "length", e.target.value, true)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.lengthErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.lengthError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.lengthError}
                            </p>
                          )}
                        </div>

                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointWidthShort", { defaultValue: "Largura" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.width || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "width", e.target.value, true)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.widthErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.widthError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.widthError}
                            </p>
                          )}
                        </div>

                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointHeightShort", { defaultValue: "Altura" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.height || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "height", e.target.value, true)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.heightErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.heightError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.heightError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ✅ Loading Access */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                      {t("loadingAccess.label", { defaultValue: "Loading access" })}
                    </label>

                    <div className="edit-pendent-load-bearer-select-wrapper" style={{ marginTop: 2, marginBottom: 10 }}>
                      <Select
                        options={loadingAccessOptions}
                        value={
                          loadingAccessOptions.find(
                            (o) => o.value === String(point.loadingAccess || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => {
                          const updated = [...pickupPoints];
                          updated[index].loadingAccess = opt?.value || null;
                          updated[index].loadingAccessError = false;
                          setPickupPoints(updated);
                          if (formError) setFormError("");
                        }}
                        isClearable
                        placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={
                          point.loadingAccessError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"
                        }
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </div>

                    {/* ✅ Handling Equipment */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                      {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                    </label>

                    <div className="edit-pendent-load-bearer-select-wrapper" style={{ marginTop: 2, marginBottom: 10 }}>
                      <Select
                        options={handlingEquipmentOptions}
                        value={
                          handlingEquipmentOptions.find(
                            (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => {
                          const updated = [...pickupPoints];
                          const heVal = opt?.value || null;

                          updated[index].handlingEquipment = heVal;

                          if (heVal !== "OTHER") {
                            updated[index].handlingEquipmentOther = "";
                            updated[index].handlingEquipmentOtherError = false;
                          } else {
                            // ✅ não mostrar erro só por selecionar OTHER
                            // só mostramos erro se já tentou guardar (submit)
                            updated[index].handlingEquipmentOtherError =
                              hasTriedSubmit && !isNonEmpty(updated[index].handlingEquipmentOther);
                          }

                          setPickupPoints(updated);
                          if (formError) setFormError("");
                        }}
                        isClearable
                        placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={"edit-pendent-load-bearer-select"}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </div>

                    {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                      <>
                        <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                          {tNonEmpty(
                            "handlingEquipment.otherLabel",
                            "Outro (especificar)*"
                          )}
                        </label>

                        <input
                          type="text"
                          placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Specify other equipment..." })}
                          value={point.handlingEquipmentOther || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            const v = e.target.value;

                            updated[index].handlingEquipmentOther = v;

                            // ✅ só marcar erro após submit; antes disso, não “castiga”
                            updated[index].handlingEquipmentOtherError = hasTriedSubmit && !isNonEmpty(v);

                            setPickupPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.handlingEquipmentOtherError ? "input-error" : ""
                          }`}
                          style={{ marginBottom: 8 }}
                        />

                        {point.handlingEquipmentOtherError && (
                          <p className="edit-pendent-load-bearer-error">
                            {t("validation.handlingEquipmentOtherRequired", {
                              defaultValue: "Please specify the other handling equipment.",
                            })}
                          </p>
                        )}
                      </>
                    )}

                    {/* 🆕 Reference (after handlingEquipment) */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                      {t("labels.reference")}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference")}
                      value={point.reference || ""}
                      onChange={(e) => {
                        const updated = [...pickupPoints];
                        updated[index].reference = e.target.value;
                        setPickupPoints(updated);
                        if (formError) setFormError("");
                      }}
                      className="edit-pendent-load-bearer-input"
                      style={{ marginBottom: 10 }}
                    />

                    {point.showMoreFields ? (
                      <>
                        <div className="edit-pendent-load-bearer-extra-fields">
                          <input
                            type="text"
                            placeholder={t("placeholders.address")}
                            value={point.address || ""}
                            onChange={(e) => {
                              const updated = [...pickupPoints];
                              updated[index].address = e.target.value;
                              updated[index].addressError = !isNonEmpty(e.target.value);
                              setPickupPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.addressError ? "input-error" : ""
                            }`}
                          />

                          <div className="edit-pendent-load-bearer-row-compact">
                            <input
                              type="text"
                              placeholder={t("placeholders.postalCode")}
                              value={point.postalCode || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].postalCode = e.target.value;
                                updated[index].postalCodeError = !isNonEmpty(e.target.value);
                                setPickupPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.postalCodeError ? "input-error" : ""
                              }`}
                            />
                            <input
                              type="text"
                              placeholder={t("placeholders.city")}
                              value={point.city || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].city = e.target.value;
                                updated[index].cityError = !isNonEmpty(e.target.value);
                                setPickupPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.cityError ? "input-error" : ""
                              }`}
                            />
                            <input
                              type="text"
                              placeholder={t("placeholders.country")}
                              value={point.country || ""}
                              onChange={(e) => {
                                const updated = [...pickupPoints];
                                updated[index].country = e.target.value;
                                updated[index].countryError = !isNonEmpty(e.target.value);
                                setPickupPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.countryError ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <input
                            type="text"
                            placeholder={t("placeholders.contactPhone")}
                            value={point.contactPhone || ""}
                            onChange={(e) => {
                              const updated = [...pickupPoints];
                              updated[index].contactPhone = e.target.value;
                              updated[index].contactPhoneError = !isNonEmpty(e.target.value);
                              setPickupPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.contactPhoneError ? "input-error" : ""
                            }`}
                          />

                          <input
                            type="text"
                            placeholder={t("placeholders.gMapsLink")}
                            value={point.gMapsLink || ""}
                            onChange={(e) => {
                              const updated = [...pickupPoints];
                              updated[index].gMapsLink = e.target.value;
                              updated[index].gMapsLinkError = !isNonEmpty(e.target.value);
                              setPickupPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.gMapsLinkError ? "input-error" : ""
                            }`}
                          />

                          <input
                            type="text"
                            placeholder={t("placeholders.coordinates")}
                            value={point.coordinates || ""}
                            onChange={(e) => {
                              const updated = [...pickupPoints];
                              updated[index].coordinates = e.target.value;
                              updated[index].coordinatesError = !isNonEmpty(e.target.value);
                              setPickupPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.coordinatesError ? "input-error" : ""
                            }`}
                          />
                        </div>

                        <div className="edit-pendent-load-bearer-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-pendent-load-bearer-toggle-fields-btn"
                            onClick={() => {
                              const updated = [...pickupPoints];
                              updated[index].showMoreFields = false;
                              setPickupPoints(updated);
                            }}
                          >
                            {t("buttons.hideFields")}{" "}
                            <FiChevronUp className="edit-pendent-load-bearer-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-pendent-load-bearer-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-pendent-load-bearer-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...pickupPoints];
                            updated[index].showMoreFields = true;
                            setPickupPoints(updated);
                          }}
                        >
                          {t("buttons.moreFields")}{" "}
                          <FiChevronDown className="edit-pendent-load-bearer-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}

              {/* Aviso quando atingir 7 */}
              {pickupPoints.length >= MAX_POINTS && (
                <p className="edit-pendent-load-bearer-error" style={{ marginTop: 6 }}>
                  {t("warnings.maxPickupPoints", { max: MAX_POINTS })}
                </p>
              )}

              {/* Botão só aparece se ainda for possível adicionar */}
              {pickupPoints.length < MAX_POINTS && (
                <button
                  type="button"
                  className="edit-pendent-load-bearer-add-location-btn"
                  onClick={() => setPickupPoints([...pickupPoints, makeEmptyPoint()])}
                >
                  {t("buttons.addPickup")}
                </button>
              )}
            </div>

            {/* COL 2: MERCHANDISE & CLIENT */}
            <div className="edit-pendent-load-bearer-column">
              <h3 className="edit-pendent-load-bearer-column-title">{t("columns.merchandise")}</h3>

              <div className="edit-pendent-load-bearer-merchandise-inline-row">
                <div className="edit-pendent-load-bearer-field-group edit-pendent-load-bearer-weight-field">
                  <label className="edit-pendent-load-bearer-label">
                    {t("labels.weight")}
                  </label>

                  <input
                    type="number"
                    value={weight}
                    onChange={handleWeightChange}
                    className={`edit-pendent-load-bearer-input ${
                      weightError ? "input-error" : ""
                    }`}
                    placeholder="0"
                  />

                  {weightError && (
                    <p className="edit-pendent-load-bearer-error">
                      {weightError}
                    </p>
                  )}
                </div>

                <div className="edit-pendent-load-bearer-field-group edit-pendent-load-bearer-merchandise-field">
                  <label className="edit-pendent-load-bearer-label">
                    {t("labels.merchandise")}
                  </label>

                  <input
                    type="text"
                    placeholder={t("placeholders.merchandise")}
                    value={merchandise}
                    onChange={(e) => {
                      setMerchandise(e.target.value);
                      if (merchandiseError) setMerchandiseError("");
                      if (formError) setFormError("");
                    }}
                    className={`edit-pendent-load-bearer-input ${
                      merchandiseError ? "input-error" : ""
                    }`}
                  />

                  {merchandiseError && (
                    <p className="edit-pendent-load-bearer-error">
                      {merchandiseError}
                    </p>
                  )}
                </div>
              </div>

              <div className="edit-pendent-load-bearer-load-dimensions-summary">
                <label className="edit-pendent-load-bearer-label edit-pendent-load-bearer-load-dimensions-title">
                  {t("labels.loadDimensions", {
                    defaultValue: "Dimensões totais da carga",
                  })}
                </label>

                <div className="edit-pendent-load-bearer-load-dimensions-row">
                  <div className="edit-pendent-load-bearer-load-dimension-field">
                    <label>
                      {t("labels.length", { defaultValue: "Comprimento (m)" })}
                    </label>
                    <input
                      type="text"
                      value={length}
                      readOnly
                      className={`edit-pendent-load-bearer-input edit-pendent-load-bearer-auto-calculated-input ${
                        lengthError ? "input-error" : ""
                      }`}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-pendent-load-bearer-load-dimension-field">
                    <label>
                      {t("labels.width", { defaultValue: "Largura (m)" })}
                    </label>
                    <input
                      type="text"
                      value={width}
                      readOnly
                      className={`edit-pendent-load-bearer-input edit-pendent-load-bearer-auto-calculated-input ${
                        widthError ? "input-error" : ""
                      }`}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-pendent-load-bearer-load-dimension-field">
                    <label>
                      {t("labels.height", { defaultValue: "Altura (m)" })}
                    </label>
                    <input
                      type="text"
                      value={height}
                      readOnly
                      className={`edit-pendent-load-bearer-input edit-pendent-load-bearer-auto-calculated-input ${
                        heightError ? "input-error" : ""
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="edit-pendent-load-bearer-dimension-warning-text">
                    {t("validation.pickupDeliveryLengthMismatch", {
                      defaultValue:
                        "⚠️ A soma dos comprimentos das cargas e descargas ainda não coincide.",
                    })}
                  </p>
                )}

                <div className="edit-pendent-load-bearer-dimension-helper-text">
                  {t("labels.loadDimensionsHelper", {
                    defaultValue:
                      "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                  })}
                </div>

                {/* Row 2: Height + Weight */}
                <div className="edit-pendent-load-bearer-merchandise-row" style={{ gap: 10 }}>
                  <div className="edit-pendent-load-bearer-field-group" style={{ flex: 1 }}>
                    <label className="edit-pendent-load-bearer-label">{t("labels.height")}</label>
                    <input
                      type="number"
                      value={height}
                      onChange={handleHeightChange}
                      className={`edit-pendent-load-bearer-input ${heightError ? "input-error" : ""}`}
                    />
                    {heightError && <p className="edit-pendent-load-bearer-error">{heightError}</p>}
                  </div>

                  <div className="edit-pendent-load-bearer-field-group" style={{ flex: 1 }}>
                    <label className="edit-pendent-load-bearer-label">{t("labels.weight")}</label>
                    <input
                      type="number"
                      value={weight}
                      onChange={handleWeightChange}
                      className={`edit-pendent-load-bearer-input ${weightError ? "input-error" : ""}`}
                    />
                    {weightError && <p className="edit-pendent-load-bearer-error">{weightError}</p>}
                  </div>
                </div>
              </div>

              {/* 🆕 Trailer Type */}
              <div className="edit-pendent-load-bearer-field-group">
                <label className="edit-pendent-load-bearer-label">
                  {t("trailerType.label", { defaultValue: "Trailer type*" })}
                </label>

                <Select
                  options={trailerTypeOptions}
                  value={
                    trailerTypeOptions.find(
                      (o) => o.value === String(trailerType || "").trim().toUpperCase()
                    ) || null
                  }
                  onChange={(opt) => {
                    const v = opt?.value || null;
                    setTrailerType(v);
                    setTrailerTypeError(false);

                    if (String(v || "").toUpperCase() !== "OTHER") {
                      setTrailerTypeOther("");
                      setTrailerTypeOtherError(false);
                    } else {
                      // ✅ não mostrar erro só por escolher OTHER
                      setTrailerTypeOtherError(hasTriedSubmit && !isNonEmpty(trailerTypeOther));
                    }

                    if (formError) setFormError("");
                  }}
                  isClearable
                  placeholder={t("trailerType.placeholder", { defaultValue: "Select trailer type..." })}
                  styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  classNamePrefix={
                    trailerTypeError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"
                  }
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />

                {trailerTypeError && (
                  <p className="edit-pendent-load-bearer-error">
                    {t("validation.trailerTypeRequired", { defaultValue: "❌ Trailer type is required." })}
                  </p>
                )}
              </div>

              {String(trailerType || "").trim().toUpperCase() === "OTHER" && (
                <div className="edit-pendent-load-bearer-field-group">
                  <label
                    className="edit-pendent-load-bearer-label edit-pendent-load-bearer-label-other"
                  >
                    {t("trailerType.otherLabel", { defaultValue: "Outro (especificar)*" })}
                  </label>

                  <input
                    type="text"
                    placeholder={t("trailerType.otherPlaceholder", { defaultValue: "Specify trailer type..." })}
                    value={trailerTypeOther || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTrailerTypeOther(v);

                      // ✅ só marcar erro após submit
                      setTrailerTypeOtherError(hasTriedSubmit && !isNonEmpty(v));

                      if (formError) setFormError("");
                    }}
                    className={`edit-pendent-load-bearer-input ${trailerTypeOtherError ? "input-error" : ""}`}
                  />

                  {trailerTypeOtherError && (
                    <p className="edit-pendent-load-bearer-error">
                      {t("validation.trailerTypeOtherRequired", { defaultValue: "❌ Please specify the trailer type." })}
                    </p>
                  )}
                </div>
              )}

              <h3 className="edit-pendent-load-bearer-column-title">{t("columns.generalInfo")}</h3>

              <div className="edit-pendent-load-bearer-field-group">
                <label className="edit-pendent-load-bearer-label-price">{t("labels.price")}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    setPriceError("");
                    if (formError) setFormError("");
                  }}
                  onBlur={() => {
                    if (price == null || String(price).trim() === "") {
                      setPriceError("");
                      return;
                    }
                    const n = Number(price);
                    if (!Number.isFinite(n) || n < 0) setPriceError(t("validation.invalidPrice"));
                    else setPriceError("");
                  }}
                  className={`edit-pendent-load-bearer-input ${priceError ? "input-error" : ""}`}
                  placeholder={t("placeholders.price")}
                />
                {priceError && <p className="edit-pendent-load-bearer-error">{priceError}</p>}
              </div>

              {showPrice4subcontracted && (
                <div className="edit-pendent-load-bearer-field-group">
                  <label className="edit-pendent-load-bearer-label-price">
                    {t("labels.price4subcontracted", {
                      defaultValue: "Price for Subcontracted",
                    })}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={price4subcontracted}
                    onChange={(e) => {
                      setPrice4subcontracted(e.target.value);
                      setPrice4subcontractedError("");
                      if (formError) setFormError("");
                    }}
                    onBlur={() => {
                      if (
                        price4subcontracted == null ||
                        String(price4subcontracted).trim() === ""
                      ) {
                        setPrice4subcontractedError("");
                        return;
                      }
                      const n = Number(price4subcontracted);
                      if (!Number.isFinite(n) || n < 0) {
                        setPrice4subcontractedError(t("validation.invalidPrice"));
                      } else {
                        setPrice4subcontractedError("");
                      }
                    }}
                    className={`edit-pendent-load-bearer-input ${
                      price4subcontractedError ? "input-error" : ""
                    }`}
                    placeholder={t("placeholders.price4subcontracted", {
                      defaultValue: "e.g. 850.00",
                    })}
                  />
                  {price4subcontractedError && (
                    <p className="edit-pendent-load-bearer-error">
                      {price4subcontractedError}
                    </p>
                  )}
                </div>
              )}

              <div className="edit-pendent-load-bearer-field-group">
                <label className="edit-pendent-load-bearer-label">{t("labels.clientType")}</label>
                <Select
                  value={
                    clientType
                      ? {
                          value: clientType,
                          label:
                            clientType === "industry" ? t("labels.industry") : t("labels.carrier"),
                        }
                      : null
                  }
                  onChange={(selected) => {
                    setClientType(selected?.value || "");
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                    setSelectedSubcontractedEmails([]);
                    setClientTypeError("");
                    setClientSelectionError("");
                    setFormError("");

                    const internalOpt = availableCarriers.find(
                      (c) => c?.isInternal || String(c?.value) === "__internal__"
                    );

                    if (internalOpt) {
                      setTransportCarrierId(internalOpt);
                      setCarrierError(false);
                    }

                    setPrice4subcontractedError("");
                  }}
                  options={[
                    { value: "industry", label: t("labels.industry") },
                    { value: "carrier", label: t("labels.carrier") },
                  ]}
                  styles={customSelectStyles}
                  isClearable
                  isSearchable={false}
                  classNamePrefix={
                    clientTypeError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"
                  }
                />
                {clientTypeError && <p className="edit-pendent-load-bearer-error">{clientTypeError}</p>}
              </div>

              {clientType && (
                <div className="edit-pendent-load-bearer-field-group">
                  <label className="edit-pendent-load-bearer-label">
                    {clientType === "industry" ? t("labels.industryClient") : t("labels.carrierClient")}
                  </label>
                  <Select
                    value={selectedClient}
                    onChange={(val) => {
                      setSelectedClient(val);
                      setSelectedClientContactEmails([]);
                      setClientSelectionError("");
                      setFormError("");
                    }}
                    options={injectedClientOptions}
                    isLoading={clientIsLoading}
                    menuIsOpen={clientMenuIsOpen}
                    styles={customSelectStyles}
                    isClearable
                    placeholder={t("placeholders.clientSearch")}
                    classNamePrefix={
                      clientSelectionError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"
                    }
                  />
                  {clientSelectionError && (
                    <p className="edit-pendent-load-bearer-error">{clientSelectionError}</p>
                  )}

                  {selectedClient?.value && (
                    <div className="edit-pendent-load-bearer-field-group">
                      <label className="edit-pendent-load-bearer-label">
                        {t("clientContactEmails.label", {
                          defaultValue: "Client contact email(s)",
                        })}
                      </label>

                      <Select
                        className="edit-pendent-load-bearer-fullwidth-select"
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={selectedClientContactEmailOptions}
                        value={selectedClientContactEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions)
                            ? selectedOptions.slice(0, 3)
                            : [];
                          setSelectedClientContactEmails(limited);
                        }}
                        placeholder={t("clientContactEmails.placeholder", {
                          defaultValue: "Select up to 3 contact email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("clientContactEmails.noOptions", {
                            defaultValue: "No contact emails found for this client.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("clientContactEmails.helper", {
                          defaultValue: "These emails come from the client's contact list.",
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {clientType && (
                <div className="edit-pendent-load-bearer-field-group">
                  <label className="edit-pendent-load-bearer-label">{t("labels.transportCarrier")}</label>
                  <Select
                    value={
                      injectedCarrierOptions.find(
                        (c) => String(c.value) === String(transportCarrierId?.value)
                      ) ||
                      transportCarrierId ||
                      null
                    }
                    onChange={(selected) => {
                      setSelectedSubcontractedEmails([]);

                      if (!selected) {
                        const internalOpt = availableCarriers.find(
                          (c) => c?.isInternal || String(c?.value) === "__internal__"
                        );
                        setTransportCarrierId(internalOpt || null);
                      } else {
                        setTransportCarrierId(selected);
                      }

                      setCarrierError(false);
                      setPrice4subcontractedError("");
                      setFormError("");
                    }}
                    options={injectedCarrierOptions}
                    isLoading={carrierIsLoading}
                    menuIsOpen={carrierMenuIsOpen}
                    isClearable
                    styles={customSelectStyles}
                    placeholder={
                      readyCarriers || transportCarrierId
                        ? t("placeholders.carrierSelect")
                        : t("placeholders.carriersLoading")
                    }
                    classNamePrefix={carrierError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"}
                  />
                  {carrierError && (
                    <p className="edit-pendent-load-bearer-error">{t("validation.transportCarrierRequired")}</p>
                  )}
                </div>
              )}

              {showPrice4subcontracted && (
                <div className="edit-pendent-load-bearer-field-group">
                  <label className="edit-pendent-load-bearer-label">
                    {t("subcontractedEmails.label", {
                      defaultValue: "Subcontracted email(s)",
                    })}
                  </label>

                  <Select
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={selectedSubcontractedEmailOptions}
                    value={selectedSubcontractedEmails}
                    onChange={(selectedOptions) => {
                      const limited = Array.isArray(selectedOptions)
                        ? selectedOptions.slice(0, 3)
                        : [];
                      setSelectedSubcontractedEmails(limited);
                    }}
                    placeholder={t("subcontractedEmails.placeholder", {
                      defaultValue: "Select up to 3 subcontracted email(s)...",
                    })}
                    noOptionsMessage={() =>
                      t("subcontractedEmails.noOptions", {
                        defaultValue: "No contact emails found for this carrier.",
                      })
                    }
                    styles={multiSelectHorizontalStyles}
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                  />

                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                    {t("subcontractedEmails.helper", {
                      defaultValue:
                        "These emails come from the selected transport carrier contact list.",
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* COL 3: DELIVERIES */}
            <div className="edit-pendent-load-bearer-column">
              <h3 className="edit-pendent-load-bearer-column-title">
                {t("columns.unloadingPoints")}
              </h3>

              {deliveryPoints.map((point, index) => {
                const prefillOption =
                  point.location || point.placeId
                    ? {
                        value: point.placeId ?? `prefill-delivery-${index}-${point.location}`,
                        label: point.location || "",
                      }
                    : null;

                const matched = point.placeId
                  ? placeOptions.find((p) => p.value === point.placeId) || null
                  : null;

                const selectValue = matched || prefillOption || null;
                const selectOptions = readyPlaces
                  ? placeOptions
                  : prefillOption
                  ? [prefillOption, ...placeOptions]
                  : placeOptions;

                return (
                  <div key={index} className="edit-pendent-load-bearer-point-group">
                    <div className="edit-pendent-load-bearer-field-group">
                    {/* Label + Delete */}
                    <div className="edit-pendent-load-label-row">
                      <label
                        className="edit-pendent-load-points-label"
                        htmlFor={`delivery-place-${index}`}
                      >
                        {t("labels.deliveryPlace")}
                      </label>

                      {deliveryPoints.length > 1 && (
                        <button
                          type="button"
                          className="edit-pendent-load-delete-btn"
                          onClick={() => removeDeliveryPlace(index)}
                          title={t("titles.removeDelivery")}
                          aria-label={t("aria.removeDelivery", { num: index + 1 })}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <CreatableSelect
                      inputId={`delivery-place-${index}`}
                      isClearable
                      isMulti={false}
                      options={selectOptions}
                      isLoading={false}
                      isDisabled={false}
                      menuIsOpen={readyPlaces ? undefined : false}
                      placeholder={t("place.placeholder")}
                      value={selectValue}
                      styles={customSelectStyles}
                      classNamePrefix={point.locationError ? "select-error" : "edit-pendent-load-bearer-select"}
                      onChange={(selectedOption) => {
                        const updated = [...deliveryPoints];

                        const clearAllFieldErrors = (pt) => {
                          pt.locationError = false;
                          pt.dateError = false;
                          pt.hourError = false;
                          pt.addressError = false;
                          pt.postalCodeError = false;
                          pt.cityError = false;
                          pt.countryError = false;
                          pt.gMapsLinkError = false;
                          pt.coordinatesError = false;
                          pt.contactPhoneError = false;
                          pt.loadingAccessError = false;
                          pt.handlingEquipmentOtherError = false;
                        };

                        let next = { ...updated[index] };

                        if (!selectedOption) {
                          next = {
                            ...next,
                            placeId: null,
                            location: "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null
                          };
                          clearAllFieldErrors(next);
                          next.showMoreFields = false;
                          updated[index] = next;
                          setDeliveryPoints(updated);
                          if (formError) setFormError("");
                          return;
                        }

                        if (selectedOption.__isNew__) {
                          next = {
                            ...next,
                            placeId: null,
                            location: selectedOption.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null
                          };
                          clearAllFieldErrors(next);
                        } else if (selectedOption.fullData) {
                          const p = selectedOption.fullData;
                          next = {
                            ...next,
                            placeId: p.id,
                            location: p.placeName || "",
                            address: p.address || "",
                            postalCode: p.postalCode || "",
                            city: p.city || "",
                            country: p.country || "",
                            gMapsLink: p.gMapsLink || "",
                            coordinates: p.coordinates || "",
                            contactPhone: p.contactPhone || "",
                            loadingAccess: p.loadingAccess ?? null
                          };
                          clearAllFieldErrors(next);
                        } else {
                          next = {
                            ...next,
                            placeId: null,
                            location: selectedOption.label || "",
                            address: "",
                            postalCode: "",
                            city: "",
                            country: "",
                            gMapsLink: "",
                            coordinates: "",
                            contactPhone: "",
                            loadingAccess: null
                          };
                          clearAllFieldErrors(next);
                        }

                        next.showMoreFields = hasAnyHiddenEmpty(next);

                        updated[index] = next;
                        setDeliveryPoints(updated);
                        if (formError) setFormError("");
                      }}
                      components={{ Option: PlaceOption, SingleValue: PlaceSingleValue }}
                    />

                    {point.locationError && (
                      <p className="edit-pendent-load-bearer-error-location">
                        {t("validation.locationRequired")}
                      </p>
                    )}

                    <div className="edit-pendent-load-bearer-datetime-group">
                      <div className="edit-pendent-load-bearer-input-wrapper">
                        <input
                          type="date"
                          value={point.date || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].date = e.target.value;
                            updated[index].dateError = false;
                            setDeliveryPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.dateError ? "input-error" : ""
                          }`}
                          ref={(el) => (inputRefs.current[`delivery-date-${index}`] = el)}
                        />
                        <FaCalendarAlt
                          className="edit-pendent-load-bearer-input-icon"
                          onClick={() => {
                            closeAllNativePickers(`delivery-date-${index}`);
                            inputRefs.current[`delivery-date-${index}`]?.focus();
                            inputRefs.current[`delivery-date-${index}`]?.showPicker?.();
                          }}
                        />
                      </div>

                      <div className="edit-pendent-load-bearer-input-wrapper">
                        <input
                          type="text"
                          placeholder="HH:MM"
                          value={point.hour || ""}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);

                            let formatted = raw;
                            if (raw.length >= 3) {
                              formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                            }

                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            const isValid = regex.test(formatted);

                            const updated = [...deliveryPoints];
                            updated[index].hour = formatted;
                            updated[index].hourError =
                              formatted.length === 5 && !isValid;

                            setDeliveryPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.hourError ? "input-error" : ""
                          }`}
                          ref={(el) => (inputRefs.current[`delivery-hour-${index}`] = el)}
                        />
                        <FaClock
                          className="edit-pendent-load-bearer-input-icon"
                          onClick={() => {
                            inputRefs.current[`delivery-hour-${index}`]?.focus();
                          }}
                        />
                      </div>
                    </div>

                    {point.dateError && (
                      <p className="edit-pendent-load-bearer-error">{t("validation.dateRequired")}</p>
                    )}
                    {point.hourError && (
                      <p className="edit-pendent-load-bearer-error">
                        {isNonEmpty(point.hour)
                          ? t("validation.hourInvalid", { defaultValue: "Invalid hour format." })
                          : t("validation.hourRequired")}
                      </p>
                    )}

                    <div className="edit-pendent-load-bearer-point-dimensions-block">
                      <label className="edit-pendent-load-bearer-label edit-pendent-load-bearer-point-dimensions-title">
                        {t("labels.pointDimensions", {
                          defaultValue: "Dimensões da carga neste ponto (em metros)",
                        })}*
                      </label>

                      <div className="edit-pendent-load-bearer-point-dimensions-row">
                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointLengthShort", { defaultValue: "Comprimento" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.length || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "length", e.target.value, false)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.lengthErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.lengthError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.lengthError}
                            </p>
                          )}
                        </div>

                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointWidthShort", { defaultValue: "Largura" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.width || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "width", e.target.value, false)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.widthErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.widthError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.widthError}
                            </p>
                          )}
                        </div>

                        <div className="edit-pendent-load-bearer-point-dimension-field">
                          <label>
                            {t("labels.pointHeightShort", { defaultValue: "Altura" })}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={point.height || ""}
                            onChange={(e) =>
                              handlePointDimensionChange(index, "height", e.target.value, false)
                            }
                            className={`edit-pendent-load-bearer-input ${
                              point.heightErrorFlag ? "input-error" : ""
                            }`}
                            placeholder="0.00m"
                          />
                          {point.heightError && (
                            <p className="edit-pendent-load-bearer-error">
                              {point.heightError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* ✅ Loading Access */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 6 }}>
                      {t("loadingAccess.label", { defaultValue: "Loading access" })}
                    </label>

                    <div className="edit-pendent-load-bearer-select-wrapper" style={{ marginTop: 2, marginBottom: 10 }}>

                      <Select
                        options={loadingAccessOptions}
                        value={
                          loadingAccessOptions.find(
                            (o) => o.value === String(point.loadingAccess || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => {
                          const updated = [...deliveryPoints];
                          updated[index].loadingAccess = opt?.value || null;
                          updated[index].loadingAccessError = false;
                          setDeliveryPoints(updated);
                          if (formError) setFormError("");
                        }}
                        isClearable
                        placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={
                          point.loadingAccessError ? "edit-pendent-load-bearer-select-error" : "edit-pendent-load-bearer-select"
                        }
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </div>

                    {/* ✅ Handling Equipment */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                      {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                    </label>

                    <div className="edit-pendent-load-bearer-select-wrapper" style={{ marginTop: 2, marginBottom: 10 }}>
                      <Select
                        options={handlingEquipmentOptions}
                        value={
                          handlingEquipmentOptions.find(
                            (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => {
                          const updated = [...deliveryPoints];
                          const heVal = opt?.value || null;

                          updated[index].handlingEquipment = heVal;

                          if (heVal !== "OTHER") {
                            updated[index].handlingEquipmentOther = "";
                            updated[index].handlingEquipmentOtherError = false;
                          } else {
                            updated[index].handlingEquipmentOtherError =
                              hasTriedSubmit && !isNonEmpty(updated[index].handlingEquipmentOther);
                          }

                          setDeliveryPoints(updated);
                          if (formError) setFormError("");
                        }}
                        isClearable
                        placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={"edit-pendent-load-bearer-select"}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </div>

                    {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                      <>
                        <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                          {tNonEmpty(
                            "handlingEquipment.otherLabel",
                            "Outro (especificar)*"
                          )}
                        </label>

                        <input
                          type="text"
                          placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Specify other equipment..." })}
                          value={point.handlingEquipmentOther || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].handlingEquipmentOther = e.target.value;
                            updated[index].handlingEquipmentOtherError = hasTriedSubmit && !isNonEmpty(e.target.value);
                            setDeliveryPoints(updated);
                            if (formError) setFormError("");
                          }}
                          className={`edit-pendent-load-bearer-input ${
                            point.handlingEquipmentOtherError ? "input-error" : ""
                          }`}
                          style={{ marginBottom: 8 }}
                        />

                        {point.handlingEquipmentOtherError && (
                          <p className="edit-pendent-load-bearer-error">
                            {t("validation.handlingEquipmentOtherRequired", {
                              defaultValue: "Please specify the other handling equipment.",
                            })}
                          </p>
                        )}
                      </>
                    )}

                    {/* 🆕 Reference (after handlingEquipment) */}
                    <label className="edit-pendent-load-bearer-label" style={{ marginBottom: 0 }}>
                      {t("labels.reference")}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference")}
                      value={point.reference || ""}
                      onChange={(e) => {
                        const updated = [...deliveryPoints];
                        updated[index].reference = e.target.value;
                        setDeliveryPoints(updated);
                        if (formError) setFormError("");
                      }}
                      className="edit-pendent-load-bearer-input"
                      style={{ marginBottom: 10 }}
                    />

                    {point.showMoreFields ? (
                      <>
                        <div className="edit-pendent-load-bearer-extra-fields">
                          <input
                            type="text"
                            placeholder={t("placeholders.address")}
                            value={point.address || ""}
                            onChange={(e) => {
                              const updated = [...deliveryPoints];
                              updated[index].address = e.target.value;
                              updated[index].addressError = !isNonEmpty(e.target.value);
                              setDeliveryPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.addressError ? "input-error" : ""
                            }`}
                          />

                          <div className="edit-pendent-load-bearer-row-compact">
                            <input
                              type="text"
                              placeholder={t("placeholders.postalCode")}
                              value={point.postalCode || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].postalCode = e.target.value;
                                updated[index].postalCodeError = !isNonEmpty(e.target.value);
                                setDeliveryPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.postalCodeError ? "input-error" : ""
                              }`}
                            />
                            <input
                              type="text"
                              placeholder={t("placeholders.city")}
                              value={point.city || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].city = e.target.value;
                                updated[index].cityError = !isNonEmpty(e.target.value);
                                setDeliveryPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.cityError ? "input-error" : ""
                              }`}
                            />
                            <input
                              type="text"
                              placeholder={t("placeholders.country")}
                              value={point.country || ""}
                              onChange={(e) => {
                                const updated = [...deliveryPoints];
                                updated[index].country = e.target.value;
                                updated[index].countryError = !isNonEmpty(e.target.value);
                                setDeliveryPoints(updated);
                                if (formError) setFormError("");
                              }}
                              className={`edit-pendent-load-bearer-input ${
                                point.countryError ? "input-error" : ""
                              }`}
                            />
                          </div>

                          <input
                            type="text"
                            placeholder={t("placeholders.contactPhone")}
                            value={point.contactPhone || ""}
                            onChange={(e) => {
                              const updated = [...deliveryPoints];
                              updated[index].contactPhone = e.target.value;
                              updated[index].contactPhoneError = !isNonEmpty(e.target.value);
                              setDeliveryPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.contactPhoneError ? "input-error" : ""
                            }`}
                          />

                          <input
                            type="text"
                            placeholder={t("placeholders.gMapsLink")}
                            value={point.gMapsLink || ""}
                            onChange={(e) => {
                              const updated = [...deliveryPoints];
                              updated[index].gMapsLink = e.target.value;
                              updated[index].gMapsLinkError = !isNonEmpty(e.target.value);
                              setDeliveryPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.gMapsLinkError ? "input-error" : ""
                            }`}
                          />

                          <input
                            type="text"
                            placeholder={t("placeholders.coordinates")}
                            value={point.coordinates || ""}
                            onChange={(e) => {
                              const updated = [...deliveryPoints];
                              updated[index].coordinates = e.target.value;
                              updated[index].coordinatesError = !isNonEmpty(e.target.value);
                              setDeliveryPoints(updated);
                              if (formError) setFormError("");
                            }}
                            className={`edit-pendent-load-bearer-input ${
                              point.coordinatesError ? "input-error" : ""
                            }`}
                          />
                        </div>

                        <div className="edit-pendent-load-bearer-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-pendent-load-bearer-toggle-fields-btn"
                            onClick={() => {
                              const updated = [...deliveryPoints];
                              updated[index].showMoreFields = false;
                              setDeliveryPoints(updated);
                            }}
                          >
                            {t("buttons.hideFields")}{" "}
                            <FiChevronUp className="edit-pendent-load-bearer-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-pendent-load-bearer-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-pendent-load-bearer-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...deliveryPoints];
                            updated[index].showMoreFields = true;
                            setDeliveryPoints(updated);
                          }}
                        >
                          {t("buttons.moreFields")}{" "}
                          <FiChevronDown className="edit-pendent-load-bearer-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}

              {/* Aviso quando atingir 7 */}
              {deliveryPoints.length >= MAX_POINTS && (
                <p className="edit-pendent-load-bearer-error" style={{ marginTop: 6 }}>
                  {t("warnings.maxDeliveryPoints", { max: MAX_POINTS })}
                </p>
              )}

              {/* Botão só aparece se ainda for possível adicionar */}
              {deliveryPoints.length < MAX_POINTS && (
                <button
                  type="button"
                  className="edit-pendent-load-bearer-add-location-btn"
                  onClick={() => setDeliveryPoints([...deliveryPoints, makeEmptyPoint()])}
                >
                  {t("buttons.addDelivery")}
                </button>
              )}
            </div>
          </div>

          {/* Notes for Driver (optional) */}
          <div className="edit-pendent-load-bearer-field-group">
            <label className="edit-pendent-load-bearer-label-notes">
              {t("labels.notes4driver")}
            </label>
            <textarea
              rows={4}
              value={notes4driver}
              onChange={(e) => {
                setNotes4driver(e.target.value);
                if (formError) setFormError("");
              }}
              placeholder={t("placeholders.notes4driver")}
              className="edit-pendent-load-bearer-input"
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Erro geral de formulário (quando algo obrigatório falha no Save) */}
          {formError && (
            <p className="edit-pendent-load-bearer-error" style={{ marginTop: 8 }}>
              {formError}
            </p>
          )}

          <div className="edit-pendent-load-bearer-button-group">
            <button
              type="button"
              onClick={() => navigate("/loads?view=pendent")}
              className="edit-pendent-load-bearer-cancel-button"
            >
              {t("buttons.cancel")}
            </button>
            <button
              type="submit"
              className="edit-pendent-load-bearer-save-button"
              disabled={
                loading ||
                lengthError ||
                heightError ||
                widthError ||
                weightError ||
                price4subcontractedError
              }
            >
              {loading ? t("buttons.saving") : t("buttons.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditPendentLoadBearer;
